import React, { useContext, useEffect, useState } from "react";
import { AppContext } from "../contexts/AppContext";
import { useHistory } from "react-router-dom";
import { fetchOrdersByLocation, fetchOrdersByOffset } from "../services/OrderService";
import useInterval from "../helpers/useInterval";
import Modes from "../contexts/Modes";
import { sortOrders } from "../helpers/Util";
import { getFromStorage } from "../services/LocalStorage";
import {
    getApplicablePickupAreaOrdersFromReadyToDispatch,
    isOrderDispatchedCheckedAsCompleted
} from "../helpers/OrderHelper";
import Themes from "../styles/Themes";

export function PollingTimer({ props }) {
    const history = useHistory();
    const {
        orders,
        setOrders,
        setIsInitialOrderLoading,
        ordersPerPage,
        pages,
        setPages,
        loggedIn,
        kitchen,
        currentPage,
        setCurrentPage,
        mode,
        waitingTime,
        setOverTimeOrderCount,
        authData,
        isAssignerMode,
        setCurrentTheme,
        setSelectedTab,
        setKitchen,
    } = useContext(AppContext);

    const pollInterval = 4000; // get this from config
    const pageSwitchInterval = 60000; // get this from config

    // const [timer, setTimer] = useState(undefined);
    const [offset, setOffset] = useState(-1);

    const [pollCount, setPollCount] = useState(0);
    const [orderResponse, setOrderResponse] = useState([]);

    const getRemoveStatuses = (order) => {
        if (mode === Modes.DRIVER_DASHBOARD) {
            return ['COMPLETED', 'CANCELLED', ...(isOrderDispatchedCheckedAsCompleted(order) ? ['DISPATCHED'] : [])];
        } else {
            const excludeReadyToDispatchMode =
                getApplicablePickupAreaOrdersFromReadyToDispatch(isAssignerMode, [order]).length > 0;
            return ['COMPLETED', 'CANCELLED', 'DISPATCHED', ...(excludeReadyToDispatchMode ? [] : ['READY_TO_DISPATCH'])];
        }
    };

    // TODO: Start timer on login only
    useInterval(async () => {
        await doPoll();
        if (mode === Modes.DISPATCHER || mode === Modes.ORDER_READY) {
            mapOrderResponse();
        }
    }, pollInterval);

    const getAcceptedTime = (order) => {

        if (order.scheduledInfo && order.scheduledInfo.scheduledAt) {
            return order.scheduledInfo.scheduledAt - (order.preparationTimeInMinutes * 60000);
        }

        const acceptedStatus = order.statusHistory.find(item => item.status === 'ACCEPTED');
        if (acceptedStatus) {
            return acceptedStatus.eventCompletedTime;
        }
        return Date.now();
    }

    const mapOrderResponse = () => {
        const currentTime = Date.now();
        let overTimeCount = 0;
        setOrders(orderResponse.map((orderToMap) => {
            let updated = orderToMap;
            if (!updated.acceptedTime) {
                updated = {
                    ...updated,
                    acceptedTime: getAcceptedTime(updated),
                };
            }
            const diff = currentTime - updated.acceptedTime;
            updated = {
                ...updated,
                elapsedTime: Math.floor(diff / 60000)
            };
            if (updated.elapsedTime > waitingTime) {
                updated = {
                    ...updated,
                    overTime: true,
                };
                overTimeCount += 1;
            }
            orders.forEach(order => {
                if(order.id === updated.id && order.tempSelected3pl) {
                    updated = {
                        ...updated,
                        tempSelected3pl: order.tempSelected3pl
                    }
                }
            })
            return updated;
        }));
        setOverTimeOrderCount(overTimeCount);
    }

    useEffect(() => {
        setOffset(-1);
        setOrderResponse([]);
        setTheme();

        if (mode === Modes.DISPATCHER) {
            setSelectedTab(0);
        }
    }, [loggedIn, mode]);


    const setTheme = () => {
        if (!authData) {
            return;
        }
        const localConfig = getFromStorage(authData.username);
        if (localConfig) {
            setCurrentTheme(Themes[localConfig.theme] || Themes['dark']);
        }
    }

    useEffect(() => {
        if (mode !== Modes.DRIVER_DASHBOARD) {
            return;
        }
        if (pollCount * pollInterval >= pageSwitchInterval) {
            // switch page
            if (currentPage >= pages.length - 1) {
                setCurrentPage(0);
            } else {
                setCurrentPage(currentPage => currentPage + 1);
            }
            setPollCount(0);
        }

    }, [pollCount]);

    const doPoll = async () => {
        if (!loggedIn) return;

        if (mode === Modes.DRIVER_DASHBOARD) {
            setPollCount(count => count + 1);
        }

        let response = undefined;
        if (offset === -1) {
            setIsInitialOrderLoading(true);
            response = await fetchOrdersByLocation(kitchen, mode, isAssignerMode);
            setOrderResponse(sortOrders(response.data));
            setIsInitialOrderLoading(false);
        } else {
            response = await fetchOrdersByOffset(kitchen, offset);
            let modified = orderResponse;
            for (let element of response.data) {
                const existingOrder = modified.find((item) => {
                    return item.id === element.id;
                });
                if (existingOrder) {
                    // decide to remove or update
                    if (getRemoveStatuses(element).includes(element.status)) {
                        modified = modified.filter((order) => {
                            return order.id !== element.id;
                        });
                    } else {
                        modified = modified.map((order) => {
                            if (order.id === element.id) {
                                return element;
                            } else {
                                return order;
                            }
                        });
                    }
                } else {
                    if (element.status === "ACCEPTED") {
                        modified = [...modified, element];
                    }
                }
            }
            setOrderResponse(modified);
        }
        if (!response) return;

        setOffset(response.offset);
        // calculatePages(response.data);
    }

    useEffect(() => {
        if (mode === Modes.DRIVER_DASHBOARD) {
            calculatePages(orderResponse);
        }
    }, [orderResponse]);

    useEffect(() => {
        if (mode === Modes.DRIVER_DASHBOARD) {
            calculatePages(orderResponse);
        }
    }, [ordersPerPage]);

    useEffect(() => {
        setOrders(orderResponse);
        // prevent navigation to no longer available pages
        if (currentPage >= pages.length) {
            setCurrentPage(pages.length === 0 ? 0 : pages.length - 1);
        }
    }, [pages]);

    const calculatePages = (orderList) => {
        const pageCount = Math.ceil(orderList.length / (ordersPerPage || 1));
        // if (pageCount === pages.length) {
        //     return;
        // }

        const pageList = [];

        for (let i = 0; i < pageCount; i++) {
            pageList.push({
                pageId: i,
                start: i * ordersPerPage,
                end: (i * ordersPerPage) + ordersPerPage - 1
            });
        }
        if (pageList.length === 0) {
            pageList.push({
                pageId: 0,
                start: 0,
                end: 0
            });
        }
        setPages(pageList);
    }

    useEffect(() => {
        if (!loggedIn) return;

        history.push("/dashboard/" + currentPage);
    }, [currentPage]);

    useEffect(() => {
        const cachedKitchen = getFromStorage('kitchen');
        if (cachedKitchen) {
          setKitchen(cachedKitchen);
        }
      }, [])

    return (
        <div></div>
    )
}
